import {
  DIAS_SEMANA,
  normalizeHorarioDetallado,
} from "./restaurantProfile";

const DATABASE_DAY_BY_ID = {
  domingo: 0,
  lunes: 1,
  martes: 2,
  miercoles: 3,
  jueves: 4,
  viernes: 5,
  sabado: 6,
};

const trimTime = (value, fallback = "") => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value || ""));
  if (!match) return fallback;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
};

const toMinutes = (value) => {
  const normalized = trimTime(value);
  if (!normalized) return null;
  const [hours, minutes] = normalized.split(":").map(Number);
  return hours * 60 + minutes;
};

export const buildBranchOperatingHoursRows = (branchId, horarioDetallado) =>
  normalizeHorarioDetallado(horarioDetallado).map((day) => {
    const shifts = day.abierto ? day.franjas.slice(0, 2) : [];
    return {
      branch_id: branchId,
      day_of_week: DATABASE_DAY_BY_ID[day.id],
      is_open: day.abierto === true,
      first_open: shifts[0] ? trimTime(shifts[0].apertura) : null,
      first_close: shifts[0] ? trimTime(shifts[0].cierre) : null,
      second_open: shifts[1] ? trimTime(shifts[1].apertura) : null,
      second_close: shifts[1] ? trimTime(shifts[1].cierre) : null,
    };
  });

export const mapBranchOperatingHoursToSchedule = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return null;
  const rowsByDay = new Map(
    rows.map((row) => [Number(row.day_of_week), row])
  );

  return DIAS_SEMANA.map((day) => {
    const row = rowsByDay.get(DATABASE_DAY_BY_ID[day.id]);
    const isOpen = row?.is_open === true;
    const shifts = isOpen
      ? [
          row?.first_open && row?.first_close
            ? {
                apertura: trimTime(row.first_open),
                cierre: trimTime(row.first_close),
              }
            : null,
          row?.second_open && row?.second_close
            ? {
                apertura: trimTime(row.second_open),
                cierre: trimTime(row.second_close),
              }
            : null,
        ].filter(Boolean)
      : [];

    return {
      id: day.id,
      label: day.label,
      abierto: isOpen && shifts.length > 0,
      franjas: shifts,
    };
  });
};

const getClockParts = (date, timezone) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone || "America/Argentina/Buenos_Aires",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = Object.fromEntries(
    formatter
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );
  const dayByShortName = {
    Sun: "domingo",
    Mon: "lunes",
    Tue: "martes",
    Wed: "miercoles",
    Thu: "jueves",
    Fri: "viernes",
    Sat: "sabado",
  };

  return {
    dayId: dayByShortName[parts.weekday] || "lunes",
    minutes: Number(parts.hour || 0) * 60 + Number(parts.minute || 0),
  };
};

const isInsideShift = (minutes, shift, previousDay = false) => {
  const opening = toMinutes(shift?.apertura);
  const closing = toMinutes(shift?.cierre);
  if (opening === null || closing === null) return false;
  if (closing > opening) {
    return !previousDay && minutes >= opening && minutes < closing;
  }
  return previousDay ? minutes < closing : minutes >= opening;
};

export const isBranchScheduleOpen = (
  horarioDetallado,
  { date = new Date(), timezone = "America/Argentina/Buenos_Aires" } = {}
) => {
  const schedule = normalizeHorarioDetallado(horarioDetallado);
  const clock = getClockParts(date, timezone);
  const todayIndex = schedule.findIndex((day) => day.id === clock.dayId);
  if (todayIndex < 0) return true;
  const today = schedule[todayIndex];
  const previousDay = schedule[(todayIndex + schedule.length - 1) % schedule.length];

  if (
    today.abierto &&
    today.franjas.some((shift) => isInsideShift(clock.minutes, shift))
  ) {
    return true;
  }

  return (
    previousDay.abierto &&
    previousDay.franjas.some((shift) =>
      isInsideShift(clock.minutes, shift, true)
    )
  );
};
